import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoginModal from './LoginModal';
import SignupFlow from './SignupFlow';
import ForgotPasswordModal from './ForgotPasswordModal';
import ConfirmModal from './ConfirmModal';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useLoginModal } from '../contexts/LoginModalContext';
import { getStoredAllUserData } from '../services/authService';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="p-2 rounded-lg bg-dark-800/60 hover:bg-dark-700 border border-dark-700 text-dark-400 hover:text-white transition-all duration-200"
      aria-label="Toggle theme"
    >
      {isDark ? (
        // Sun icon
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
        </svg>
      ) : (
        // Moon icon
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

// Check if avatar is an uploaded image URL or data URL
const isImageAvatar = (avatar: string | null | undefined): boolean => {
  if (!avatar) return false;
  return avatar.startsWith('data:image') || avatar.startsWith('http') || avatar.startsWith('/uploads');
};

// User Avatar with Dropdown Component
interface UserAvatarDropdownProps {
  onLogout: () => void;
}

const UserAvatarDropdown: React.FC<UserAvatarDropdownProps> = ({ onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const user = getStoredAllUserData()?.user;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = [
    {
      id: 'wallet',
      label: t('header.wallet') || 'Wallet',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      onClick: () => { navigate('/wallet'); setIsOpen(false); }
    },
    {
      id: 'mypage',
      label: t('header.myPage') || 'My Page',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      onClick: () => { navigate('/my-page'); setIsOpen(false); }
    },
    {
      id: 'messages',
      label: t('header.messages') || 'Messages',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      onClick: () => { navigate('/message'); setIsOpen(false); }
    },
    {
      id: 'history',
      label: t('header.history') || 'Betting History',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => { navigate('/bet-history'); setIsOpen(false); }
    }
  ];

  const userAvatar = user?.avatar;
  const hasImageAvatar = isImageAvatar(userAvatar);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-full hover:bg-white/5 transition-all duration-200 group"
      >
        {/* Avatar Circle */}
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-emerald-500 flex items-center justify-center ring-2 ring-white/10 group-hover:ring-primary-500/50 transition-all duration-200 overflow-hidden">
            {hasImageAvatar ? (
              <img src={userAvatar!} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>
          {/* Online Indicator */}
          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-dark-900"></div>
        </div>
        
        {/* Dropdown Arrow */}
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-dark-800 rounded-xl shadow-xl border border-white/10 py-2 z-50 animate-fadeIn">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-emerald-500 flex items-center justify-center overflow-hidden">
                {hasImageAvatar ? (
                  <img src={userAvatar!} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{user?.name || user?.loginId || t('common.user')}</p>
                <p className="text-gray-400 text-xs truncate">{user?.email || ''}</p>
              </div>
            </div>
            {/* Balance Display */}
            <div className="mt-3 flex items-center justify-between bg-dark-700/50 rounded-lg px-3 py-2">
              <span className="text-gray-400 text-xs">{t('header.balance')}</span>
              <span className="text-primary-400 font-semibold text-sm">${user?.balance?.money?.toLocaleString() || '0'}</span>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={item.onClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-150"
              >
                <span className="text-gray-400">{item.icon}</span>
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-white/5 pt-1">
            <button
              onClick={() => { onLogout(); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors duration-150"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-sm">{t('common.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface HeaderProps {
  onScrollToSection: (sectionId: string) => void;
  onToggleSidebar: () => void;
  onClearSidebarActive: () => void;
  activeButton: string | null;
  onSetActiveButton: (buttonId: string) => void;
}

// Balance Display with Deposit Button Component
interface BalanceDisplayProps {
  variant?: 'desktop' | 'mobile';
}

const BalanceDisplay: React.FC<BalanceDisplayProps> = ({ variant = 'desktop' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setUpdateTrigger] = React.useState(0);
  const user = getStoredAllUserData()?.user;
  const balance = user?.balance?.money ?? 0;

  // Listen for balance update events
  React.useEffect(() => {
    const handleBalanceUpdate = () => {
      setUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('userBalanceUpdated', handleBalanceUpdate);
    return () => {
      window.removeEventListener('userBalanceUpdated', handleBalanceUpdate);
    };
  }, []);

  if (variant === 'mobile') {
    return (
      <div className="flex items-center bg-dark-800/90 rounded-lg border border-white/5 overflow-hidden">
        {/* Balance */}
        <button 
          onClick={() => navigate('/wallet')}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white font-semibold text-[10px]">${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </button>
        
        {/* Divider */}
        <div className="w-px h-5 bg-white/10"></div>
        
        {/* Deposit Button */}
        <button
          onClick={() => navigate('/wallet?tab=deposit')}
          className="flex items-center gap-1 px-2 py-1.5 bg-gradient-to-r from-primary-600 to-emerald-500 hover:from-primary-500 hover:to-emerald-400 text-white transition-all duration-200"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    );
  }

  // Desktop variant
  return (
    <div className="flex items-center bg-dark-800/80 backdrop-blur-sm rounded-2xl border border-white/5 overflow-hidden shadow-lg">
      {/* Balance Section */}
      <button 
        onClick={() => navigate('/wallet')}
        className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors group"
      >
        {/* Balance */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-500/20 to-emerald-500/20 flex items-center justify-center border border-primary-500/20">
            <svg className="w-3.5 h-3.5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex flex-col items-start">
            <span className="text-gray-400 text-[9px] uppercase tracking-wider font-medium">{t('header.balance') || 'Balance'}</span>
            <span className="text-white font-bold text-xs group-hover:text-primary-400 transition-colors">
              ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </button>
      
      {/* Divider */}
      <div className="w-px h-10 bg-white/10"></div>
      
      {/* Deposit Button */}
      <button
        onClick={() => navigate('/wallet?tab=deposit')}
        className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-600 to-emerald-500 hover:from-primary-500 hover:to-emerald-400 text-white transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/30"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
        <span className="text-sm font-semibold">{t('header.deposit') || 'Deposit'}</span>
      </button>
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ onScrollToSection, onToggleSidebar, onClearSidebarActive, activeButton, onSetActiveButton }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, logout: contextLogout } = useAuth();
  const { isLoginModalOpen, openLoginModal, closeLoginModal } = useLoginModal();
  
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  
  // Extract code from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const referralCode = searchParams.get('code') || '';

  // Update time every second
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const timezoneOffset = new Date().getTimezoneOffset();
      const gmtOffset = timezoneOffset / -60;
      const gmtString = gmtOffset >= 0 ? `GMT+${gmtOffset.toString().padStart(2, '0')}` : `GMT${gmtOffset.toString().padStart(2, '0')}`;
      setCurrentDateTime(`${timeString} ${gmtString}`);
    };

    // Set initial time
    updateDateTime();

    // Update time every second
    const interval = setInterval(updateDateTime, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // Auto-open signup modal when code parameter is present and user is not authenticated
  useEffect(() => {
    if (referralCode && !isAuthenticated) {
      setIsSignupModalOpen(true);
    }
  }, [referralCode, isAuthenticated]);


  const handleLoginClick = () => {
    openLoginModal();
  };

  const handleSignupClick = () => {
    setIsSignupModalOpen(true);
  };

  const handleSwitchToSignup = () => {
    closeLoginModal();
    setIsSignupModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupModalOpen(false);
    setIsForgotPasswordModalOpen(false);
    openLoginModal();
  };

  const handleSwitchToForgotPassword = () => {
    closeLoginModal();
    setIsForgotPasswordModalOpen(true);
  };

  const handleLogout = useCallback(() => {
    contextLogout();
    closeLoginModal();
    setIsSignupModalOpen(false);
    navigate('/');
  }, [contextLogout, closeLoginModal, navigate]);

  const handleLogoutClick = useCallback(() => setLogoutConfirmOpen(true), []);

  const handleSuccessfulLogin = () => {
    closeLoginModal();
  };

  const handleSuccessfulSignup = () => {
    setIsSignupModalOpen(false);
  };

  return (
    <>
      {/* Desktop Header */}
      <div className="hidden lg:block bg-dark-900/95 backdrop-blur-xl border-b border-white/5 w-full">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Time */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Logo />
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-800/50 rounded-lg border border-white/5">
                <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                <span className="text-primary-400 text-sm font-mono tracking-wider">
                  {currentDateTime}
                </span>
              </div>
            </div>

            {/* Login/Signup Buttons or User Avatar */}
            <div className="flex items-center space-x-3">
              <ThemeToggle />
              {isAuthenticated ? (
                <div className="flex items-center space-x-2">
                  {/* Balance & Deposit */}
                  <BalanceDisplay />
                  <UserAvatarDropdown onLogout={handleLogoutClick} />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button 
                    onClick={handleLoginClick}
                    className="px-5 py-2 bg-dark-700/80 hover:bg-dark-600 text-gray-300 hover:text-white rounded-xl text-sm font-medium transition-all duration-200 border border-white/5 hover:border-white/10"
                  >
                    {t('common.login')}
                  </button>
                  <button 
                    onClick={handleSignupClick}
                    className="px-5 py-2 bg-gradient-to-r from-primary-600 to-emerald-500 hover:from-primary-500 hover:to-emerald-400 text-white rounded-xl text-sm font-semibold transition-all duration-300 shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 hover:scale-[1.02]"
                  >
                    {t('common.signup')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header - Premium Design */}
      <div className="lg:hidden bg-dark-900/95 backdrop-blur-xl border-b border-white/5 w-full">
        <div className="px-3 py-2.5">
          {/* Top Row: Hamburger, Logo, Language, Auth */}
          <div className="flex items-center justify-between">
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center gap-2">
              <button
                className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-200"
                aria-label="Toggle navigation menu"
                onClick={() => onToggleSidebar()}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <Logo />
            </div>
            
            {/* Right: Auth Buttons */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  <BalanceDisplay variant="mobile" />
                  <UserAvatarDropdown onLogout={handleLogoutClick} />
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button 
                    onClick={handleLoginClick}
                    className="px-3 py-1.5 bg-dark-700/80 hover:bg-dark-600 text-gray-300 hover:text-white rounded-lg text-xs font-medium transition-all duration-200 border border-white/5"
                  >
                    {t('common.login')}
                  </button>
                  <button 
                    onClick={handleSignupClick}
                    className="px-3 py-1.5 bg-gradient-to-r from-primary-600 to-emerald-500 text-white rounded-lg text-xs font-semibold transition-all duration-200 shadow-md shadow-primary-500/20"
                  >
                    {t('common.signup')}
                  </button>
                </div>
              )}
            </div>
          </div>
          
         
        </div>
      </div>

      {/* Modals */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
        onSwitchToSignup={handleSwitchToSignup}
        onSwitchToForgotPassword={handleSwitchToForgotPassword}
        onLoginSuccess={handleSuccessfulLogin}
      />
      <SignupFlow 
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        onSwitchToLogin={handleSwitchToLogin}
        onSignupSuccess={handleSuccessfulSignup}
        code={referralCode}
      />
      <ForgotPasswordModal
        isOpen={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
        onBackToLogin={handleSwitchToLogin}
      />
      <ConfirmModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
        title={t('common.confirmLogoutTitle')}
        message={t('common.confirmLogoutMessage')}
        variant="danger"
        cancelLabel={t('common.close')}
        confirmLabel={t('common.logout')}
      />
    </>
  );
};

export default Header; 